import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { SupplierInvoiceAllocations } from "@/components/finance/SupplierInvoiceAllocations";
import { SupplierInvoiceAudit } from "@/components/finance/SupplierInvoiceAudit";
import { SupplierInvoiceDocuments } from "@/components/finance/SupplierInvoiceDocuments";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SupplierInvoiceLines } from "@/components/finance/SupplierInvoiceLines";
import { SupplierInvoicePayments } from "@/components/finance/SupplierInvoicePayments";
import { SupplierInvoiceStatusBadge } from "@/components/finance/SupplierInvoiceStatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { hasCapability } from "@shared/finance/capabilities";
import type { FinanceRole } from "@shared/finance/capabilities";
import {
  LOCKED_HEADER_STATUSES,
  MANUAL_STATUS_TRANSITIONS,
  requiredPermissionForSupplierInvoiceTransition,
} from "@shared/finance/supplierInvoices";
import type { SupplierInvoiceStatus } from "@shared/finance/supplierInvoices";
import { ArrowLeft, Loader2, Pencil, Save } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useLocation, useRoute } from "wouter";

const STATUS_LABELS: Record<SupplierInvoiceStatus, string> = {
  draft: "Borrador",
  received: "Recibida",
  pending_review: "Pendiente revisión",
  approved: "Aprobada",
  scheduled_for_payment: "Programada para pago",
  partially_paid: "Parcialmente pagada",
  paid: "Pagada",
  cancelled: "Cancelada",
};

const NO_CATEGORY_VALUE = "none";

function formatEUR(amount: string | number | null | undefined) {
  if (amount === null || amount === undefined) return "-";
  const n = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(n)) return "-";
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);
}

function formatDate(d: string | null | undefined) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("es-ES");
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</dt>
      <dd className="mt-1 text-sm">{value ?? "-"}</dd>
    </div>
  );
}

interface EditHeaderFormProps {
  invoiceId: number;
  invoice: Record<string, unknown>;
  onClose: () => void;
}

function EditHeaderForm({ invoiceId, invoice, onClose }: EditHeaderFormProps) {
  const utils = trpc.useUtils();
  const categoriesQuery = trpc.financialCategories.list.useQuery({ includeInactive: false });
  const [form, setForm] = useState({
    invoiceNumber: String(invoice.invoiceNumber ?? ""),
    invoiceDate: String(invoice.invoiceDate ?? ""),
    expectedPaymentDate: String(invoice.expectedPaymentDate ?? ""),
    taxableBase: String(invoice.taxableBase ?? ""),
    vatAmount: String(invoice.vatAmount ?? ""),
    totalAmount: String(invoice.totalAmount ?? ""),
    financialCategoryId: (invoice.financialCategoryId as number | null) ?? null,
    notes: String(invoice.notes ?? ""),
  });

  const updateDraft = trpc.supplierInvoices.updateDraft.useMutation({
    onSuccess: () => {
      toast.success("Factura actualizada");
      utils.supplierInvoices.getById.invalidate({ invoiceId });
      onClose();
    },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  function set(field: keyof typeof form) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((p) => ({ ...p, [field]: e.target.value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    updateDraft.mutate({
      invoiceId,
      invoiceNumber: form.invoiceNumber,
      invoiceDate: form.invoiceDate || undefined,
      expectedPaymentDate: form.expectedPaymentDate || undefined,
      taxableBase: form.taxableBase ? parseFloat(form.taxableBase) : undefined,
      vatAmount: form.vatAmount ? parseFloat(form.vatAmount) : undefined,
      totalAmount: form.totalAmount ? parseFloat(form.totalAmount) : undefined,
      financialCategoryId: form.financialCategoryId,
      notes: form.notes || undefined,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="edit-invoice-num">N.º factura</Label>
          <Input id="edit-invoice-num" value={form.invoiceNumber} onChange={set("invoiceNumber")} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="edit-invoice-date">Fecha factura</Label>
          <Input id="edit-invoice-date" type="date" value={form.invoiceDate} onChange={set("invoiceDate")} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="edit-exp-date">Fecha vencimiento</Label>
        <Input id="edit-exp-date" type="date" value={form.expectedPaymentDate} onChange={set("expectedPaymentDate")} />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="edit-base">Base imponible</Label>
          <Input id="edit-base" type="number" step="0.01" min="0" value={form.taxableBase} onChange={set("taxableBase")} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="edit-vat">IVA</Label>
          <Input id="edit-vat" type="number" step="0.01" min="0" value={form.vatAmount} onChange={set("vatAmount")} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="edit-total">Total</Label>
          <Input id="edit-total" type="number" step="0.01" min="0" value={form.totalAmount} onChange={set("totalAmount")} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="edit-category">Categoría</Label>
        <Select
          value={form.financialCategoryId?.toString() ?? NO_CATEGORY_VALUE}
          onValueChange={(val) => setForm((p) => ({ ...p, financialCategoryId: val === NO_CATEGORY_VALUE ? null : parseInt(val, 10) }))}
        >
          <SelectTrigger id="edit-category">
            <SelectValue placeholder="Sin categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_CATEGORY_VALUE}>Sin categoría</SelectItem>
            {(categoriesQuery.data ?? []).map((cat: { id: number; name: string }) => (
              <SelectItem key={cat.id} value={String(cat.id)}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="edit-notes">Notas</Label>
        <Textarea id="edit-notes" value={form.notes} onChange={set("notes")} rows={3} />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
        <Button type="submit" disabled={updateDraft.isPending}>
          {updateDraft.isPending ? "Guardando..." : <><Save className="h-4 w-4 mr-1" />Guardar</>}
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function SupplierInvoiceDetail() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [, params] = useRoute("/accounting/supplier-invoices/:id");
  const invoiceId = parseInt(params?.id ?? "0", 10);

  const canRead = user ? hasCapability(user.role as FinanceRole, "finance.read") : false;
  const canWrite = user ? hasCapability(user.role as FinanceRole, "finance.write") : false;
  const canApprove = user ? hasCapability(user.role as FinanceRole, "finance.approve") : false;

  const [editOpen, setEditOpen] = useState(false);
  const [transitionDialog, setTransitionDialog] = useState<{ to: SupplierInvoiceStatus } | null>(null);
  const [transitionReason, setTransitionReason] = useState("");

  const utils = trpc.useUtils();

  const query = trpc.supplierInvoices.getById.useQuery(
    { invoiceId },
    { enabled: canRead && invoiceId > 0 }
  );

  const paymentsQuery = trpc.supplierInvoices.listPayments.useQuery(
    { invoiceId },
    { enabled: canRead && invoiceId > 0 }
  );

  const transitionMutation = trpc.supplierInvoices.transitionStatus.useMutation({
    onSuccess: () => {
      toast.success("Estado actualizado");
      utils.supplierInvoices.getById.invalidate({ invoiceId });
      setTransitionDialog(null);
      setTransitionReason("");
    },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  if (!canRead) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <p className="text-lg font-medium">Acceso denegado</p>
          <Button variant="outline" onClick={() => setLocation("/")}>Volver al inicio</Button>
        </div>
      </DashboardLayout>
    );
  }

  if (!invoiceId) {
    return (
      <DashboardLayout>
        <div className="text-center py-20 text-muted-foreground">ID de factura inválido.</div>
      </DashboardLayout>
    );
  }

  if (query.isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (query.isError || !query.data) {
    return (
      <DashboardLayout>
        <div className="text-center py-20">
          <p className="text-destructive">{query.error?.message ?? "Factura no encontrada"}</p>
          <Button variant="outline" className="mt-4" onClick={() => setLocation("/accounting/supplier-invoices")}>
            Volver al listado
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const aggregate = query.data as Record<string, unknown>;
  const invoice = aggregate.invoice as Record<string, unknown>;
  const status = String(invoice.status ?? "draft") as SupplierInvoiceStatus;
  const isLocked = LOCKED_HEADER_STATUSES.has(status);
  const canEditHeader = canWrite && !isLocked;

  const validTransitions = MANUAL_STATUS_TRANSITIONS[status] ?? [];

  function canPerformTransition(to: SupplierInvoiceStatus): boolean {
    const required = requiredPermissionForSupplierInvoiceTransition(status, to);
    if (required === "approve") return canApprove;
    return canWrite;
  }

  const lines = (aggregate.lines as Array<Record<string, unknown>>) ?? [];
  const allocations = (aggregate.allocations as Array<Record<string, unknown>>) ?? [];
  const documents = (aggregate.documents as Array<Record<string, unknown>>) ?? [];
  const auditLog = (aggregate.auditLog as Array<Record<string, unknown>>) ?? [];
  const payments = (paymentsQuery.data as Array<Record<string, unknown>>) ?? [];

  function handleTransitionConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (!transitionDialog) return;
    transitionMutation.mutate({
      id: invoiceId,
      status: transitionDialog.to,
      reason: transitionReason || undefined,
    });
  }

  const CONFIRMS_NEEDED = new Set<SupplierInvoiceStatus>(["cancelled", "approved"]);

  return (
    <DashboardLayout>
      <div className="space-y-4 max-w-5xl mx-auto">
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button variant="ghost" size="sm" onClick={() => setLocation("/accounting/supplier-invoices")}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Volver
          </Button>
          <div className="flex items-center gap-2 flex-wrap">
            <SupplierInvoiceStatusBadge status={status} />
            {canEditHeader && (
              <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
                <Pencil className="h-4 w-4 mr-1" />
                Editar cabecera
              </Button>
            )}
            {validTransitions.map((to) => {
              if (!canPerformTransition(to)) return null;
              const needsConfirm = CONFIRMS_NEEDED.has(to);
              return (
                <Button
                  key={to}
                  size="sm"
                  variant={to === "cancelled" ? "destructive" : "default"}
                  onClick={() => {
                    if (needsConfirm) {
                      setTransitionDialog({ to });
                    } else {
                      transitionMutation.mutate({ id: invoiceId, status: to });
                    }
                  }}
                  disabled={transitionMutation.isPending}
                >
                  {STATUS_LABELS[to]}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Summary card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              Factura {invoice.invoiceNumber ? `N.º ${invoice.invoiceNumber}` : `#${invoice.id}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Proveedor" value={invoice.supplierName ? String(invoice.supplierName) : `ID ${invoice.supplierId}`} />
              <Field label="Fecha factura" value={formatDate(invoice.invoiceDate as string)} />
              <Field label="Vencimiento" value={formatDate(invoice.expectedPaymentDate as string | null)} />
              <Field label="Moneda" value={String(invoice.currency ?? "EUR")} />
              <Field label="Base imponible" value={formatEUR(invoice.taxableBase as string)} />
              <Field label="IVA" value={formatEUR(invoice.vatAmount as string)} />
              <Field label="Total" value={<span className="text-base font-semibold">{formatEUR(invoice.totalAmount as string)}</span>} />
              <Field label="Origen" value={String(invoice.source ?? "manual")} />
              {!!invoice.categoryName && (
                <Field label="Categoría" value={String(invoice.categoryName)} />
              )}
            </dl>
            {!!invoice.notes && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Notas</p>
                <p className="mt-1 text-sm whitespace-pre-wrap">{String(invoice.notes)}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="lineas">
          <TabsList className="w-full overflow-x-auto">
            <TabsTrigger value="lineas">Líneas ({lines.length})</TabsTrigger>
            <TabsTrigger value="imputaciones">Imputaciones ({allocations.length})</TabsTrigger>
            <TabsTrigger value="pagos">Pagos ({payments.filter((p) => !p.deletedAt).length})</TabsTrigger>
            <TabsTrigger value="documentos">Documentos ({documents.filter((d) => !d.deletedAt).length})</TabsTrigger>
            <TabsTrigger value="historial">Historial</TabsTrigger>
          </TabsList>

          <TabsContent value="lineas" className="mt-4">
            <SupplierInvoiceLines
              invoiceId={invoiceId}
              status={status}
              lines={lines as unknown as Parameters<typeof SupplierInvoiceLines>[0]["lines"]}
              canWrite={canWrite}
            />
          </TabsContent>

          <TabsContent value="imputaciones" className="mt-4">
            <SupplierInvoiceAllocations
              invoiceId={invoiceId}
              allocations={allocations as unknown as Parameters<typeof SupplierInvoiceAllocations>[0]["allocations"]}
              totalAmount={String(invoice.totalAmount ?? "0")}
              taxableBase={String(invoice.taxableBase ?? "0")}
              vatAmount={String(invoice.vatAmount ?? "0")}
              canWrite={canWrite}
            />
          </TabsContent>

          <TabsContent value="pagos" className="mt-4">
            <SupplierInvoicePayments
              invoiceId={invoiceId}
              status={status}
              payments={payments as unknown as Parameters<typeof SupplierInvoicePayments>[0]["payments"]}
              canWrite={canWrite}
            />
          </TabsContent>

          <TabsContent value="documentos" className="mt-4">
            <SupplierInvoiceDocuments
              invoiceId={invoiceId}
              documents={documents as unknown as Parameters<typeof SupplierInvoiceDocuments>[0]["documents"]}
              canWrite={canWrite}
            />
          </TabsContent>

          <TabsContent value="historial" className="mt-4">
            <SupplierInvoiceAudit auditLog={auditLog as Parameters<typeof SupplierInvoiceAudit>[0]["auditLog"]} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit header dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar cabecera de factura</DialogTitle>
          </DialogHeader>
          <EditHeaderForm invoiceId={invoiceId} invoice={invoice} onClose={() => setEditOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Transition confirm dialog */}
      <Dialog open={!!transitionDialog} onOpenChange={(o) => { if (!o) setTransitionDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Confirmar: {transitionDialog ? STATUS_LABELS[transitionDialog.to] : ""}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTransitionConfirm} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              ¿Estás seguro de que deseas cambiar el estado a{" "}
              <strong>{transitionDialog ? STATUS_LABELS[transitionDialog.to] : ""}</strong>?
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="transition-reason">Motivo (opcional)</Label>
              <Textarea
                id="transition-reason"
                value={transitionReason}
                onChange={(e) => setTransitionReason(e.target.value)}
                rows={3}
                placeholder="Motivo del cambio..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTransitionDialog(null)}>Cancelar</Button>
              <Button
                type="submit"
                variant={transitionDialog?.to === "cancelled" ? "destructive" : "default"}
                disabled={transitionMutation.isPending}
              >
                {transitionMutation.isPending ? "Procesando..." : "Confirmar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
